import { calcularFinanzasPropiedad } from "./finanzasPropiedad";
import { calcularProrrateoParaPropiedad } from "./prorrateoCostos";
import { resolverContextoFinanciero, safeMoneyNumber } from "./contextoFinanciero";
import {
  deudaPerteneceAPropiedad,
  distribuirDeudasParaPropiedad,
  esPropiedadVendida,
  participaInventarioKye,
} from "./compatFinanciera";

const SIN_PROYECTO_ID = "sin_proyecto";

const getProyectoFromPropiedad = (propiedad = {}) => ({
  id: propiedad.proyectoId || SIN_PROYECTO_ID,
  nombre: propiedad.proyectoNombre || propiedad.proyectoId || "Sin proyecto",
});

const emptyProyecto = (id, nombre) => ({
  id,
  nombre,
  propiedades: 0,
  fraccionamientos: 0,
  obras: 0,
  lotesVendidos: 0,
  ingresoVendido: 0,
  ingresoProyectado: 0,
  ingresoCobrado: 0,
  saldoPorCobrar: 0,
  costosDirectos: 0,
  costosProrrateados: 0,
  costoTotalCapitalizable: 0,
  utilidadProyectada: 0,
  utilidadRealizada: 0,
  margenPromedio: null,
  puntoEquilibrioPendiente: 0,
  recuperacionCapital: null,
  fraccionamientoIds: [],
  obraIds: [],
});

const ensureProyecto = (map, id, nombre) => {
  if (!map.has(id)) map.set(id, emptyProyecto(id, nombre));
  return map.get(id);
};

const pushUnique = (arr, value) => {
  if (value && !arr.includes(value)) arr.push(value);
};

export const construirResumenProyectos = ({
  propiedades = [],
  deudas = [],
  gastos = [],
  catalogos = {},
} = {}) => {
  const rowsMap = new Map();
  const propiedadesActivas = propiedades.filter((p) => p.activo !== false);

  propiedadesActivas.forEach((propiedad) => {
    const proyecto = getProyectoFromPropiedad(propiedad);
    const row = ensureProyecto(rowsMap, proyecto.id, proyecto.nombre);
    const deudasPropiedadBase = deudas.filter((deuda) =>
      deudaPerteneceAPropiedad(deuda, propiedad.id)
    );
    const deudasPropiedad = distribuirDeudasParaPropiedad(
      deudasPropiedadBase,
      propiedad,
      propiedadesActivas
    );
    const gastosDirectos = gastos.filter((gasto) => gasto.propiedadId === propiedad.id);
    const prorrateo = calcularProrrateoParaPropiedad({
      propiedad,
      gastos,
      propiedades,
      catalogos,
    });
    const finanzas = calcularFinanzasPropiedad({
      propiedad,
      deudas: deudasPropiedad,
      gastosDirectos,
      costosProrrateados: prorrateo.totalProrrateado,
    });

    const participaInventario = participaInventarioKye(propiedad);
    row.propiedades += participaInventario ? 1 : 0;
    row.lotesVendidos += participaInventario && esPropiedadVendida(propiedad) ? 1 : 0;
    row.ingresoVendido += finanzas.ingresoVendido;
    row.ingresoProyectado += finanzas.ingresoProyectado;
    row.ingresoCobrado += finanzas.totalCobrado;
    row.saldoPorCobrar += finanzas.saldoPendiente;
    row.costosDirectos += finanzas.costosDirectosCapitalizables;
    row.costosProrrateados += finanzas.costosProrrateados;
    row.costoTotalCapitalizable += finanzas.costoTotalAsignado;
    row.utilidadProyectada += finanzas.utilidadProyectadaConProrrateo;
    row.utilidadRealizada += finanzas.utilidadRealizadaConProrrateo;
    pushUnique(row.fraccionamientoIds, propiedad.fraccionamientoId || propiedad.fraccNombre);
  });

  gastos.forEach((gasto) => {
    const contexto = resolverContextoFinanciero(gasto, catalogos);
    if (!contexto.proyectoId) return;

    const row = ensureProyecto(
      rowsMap,
      contexto.proyectoId,
      contexto.proyectoNombre || contexto.proyectoId
    );
    pushUnique(row.fraccionamientoIds, contexto.fraccionamientoId);
    pushUnique(row.obraIds, contexto.obraId);

    const yaIncluidoPorLote =
      gasto.propiedadId ||
      (contexto.nivel === "propiedad" && contexto.propiedadId);
    if (yaIncluidoPorLote) return;

    const metodo = gasto.metodoProrrateo || "ninguno";
    const seProrrateaALotes = gasto.capitalizable === true && metodo !== "ninguno";
    if (!seProrrateaALotes) {
      const importe = safeMoneyNumber(gasto.importe);
      if (gasto.capitalizable === true) {
        row.costoTotalCapitalizable += importe;
        row.costosProrrateados += importe;
        row.utilidadProyectada -= importe;
        row.utilidadRealizada -= importe;
      }
    }
  });

  for (const row of rowsMap.values()) {
    row.fraccionamientos = row.fraccionamientoIds.length;
    row.obras = row.obraIds.length;
    row.margenPromedio =
      row.ingresoProyectado > 0 ? row.utilidadProyectada / row.ingresoProyectado : null;
    row.puntoEquilibrioPendiente = Math.max(
      row.costoTotalCapitalizable - row.ingresoCobrado,
      0
    );
    row.recuperacionCapital =
      row.costoTotalCapitalizable > 0
        ? row.ingresoCobrado / row.costoTotalCapitalizable
        : null;
  }

  const rows = Array.from(rowsMap.values())
    .map(({ fraccionamientoIds, obraIds, ...row }) => row)
    .sort((a, b) => safeMoneyNumber(b.ingresoVendido) - safeMoneyNumber(a.ingresoVendido));

  const resumen = rows.reduce(
    (acc, row) => {
      acc.propiedades += row.propiedades;
      acc.fraccionamientos += row.fraccionamientos;
      acc.obras += row.obras;
      acc.lotesVendidos += row.lotesVendidos;
      acc.ingresoVendido += row.ingresoVendido;
      acc.ingresoProyectado += row.ingresoProyectado;
      acc.ingresoCobrado += row.ingresoCobrado;
      acc.saldoPorCobrar += row.saldoPorCobrar;
      acc.costoTotalCapitalizable += row.costoTotalCapitalizable;
      acc.utilidadProyectada += row.utilidadProyectada;
      acc.utilidadRealizada += row.utilidadRealizada;
      acc.puntoEquilibrioPendiente += row.puntoEquilibrioPendiente;
      return acc;
    },
    {
      proyectos: rows.length,
      propiedades: 0,
      fraccionamientos: 0,
      obras: 0,
      lotesVendidos: 0,
      ingresoVendido: 0,
      ingresoProyectado: 0,
      ingresoCobrado: 0,
      saldoPorCobrar: 0,
      costoTotalCapitalizable: 0,
      utilidadProyectada: 0,
      utilidadRealizada: 0,
      margenPromedio: null,
      puntoEquilibrioPendiente: 0,
      recuperacionCapital: null,
    }
  );

  resumen.margenPromedio =
    resumen.ingresoProyectado > 0
      ? resumen.utilidadProyectada / resumen.ingresoProyectado
      : null;
  resumen.recuperacionCapital =
    resumen.costoTotalCapitalizable > 0
      ? resumen.ingresoCobrado / resumen.costoTotalCapitalizable
      : null;

  return { resumen, rows };
};
